import {
  type ChangeEvent,
  type MouseEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { Box, Eye, Pencil, Plus, Rotate3d, Search, Share2, Trash2, X } from "lucide-react"
import { useAppSelector } from "hooks/storeHooks"
import { selectUserInfo } from "store/user/userSlice"
import { withListPage } from "utils/withListPage"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Switch } from "@/components/ui/switch"
import { ProductStatusBadge } from "@/components/ProductStatusBadge"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Skeleton } from "@/components/ui/skeleton"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import EmptyList from "components/EmptyList"
import Paginator from "components/Paginator"
import { useAlert } from "providers/AlertProvider"
import { AdminRequestError } from "services/adminApi"
import {
  useDeleteViewer3DProductMutation,
  useEnsureViewer3DDemoKeyMutation,
  useToggleViewer3DPublishMutation,
  useViewer3DProductsQuery,
} from "@/features/viewer-3d/api/useViewer3D"
import { buildViewer3DDemoUrl, resolveViewer3DDemoTarget } from "@/features/viewer-3d/demoUrl"
import type { Viewer3DListItem } from "@/features/viewer-3d/types"

const PAGE_SIZE_OPTIONS = [30, 50, 100]
const PAGE_SIZE_STORAGE_KEY = "viewer3dPageSize"
// Verbatim from the design (Figma 13616-10247) — QA diffs this copy literally
// against the mock (Asana 1216784411112817).
const SETUP_STEPS = ["Add products", "Set Up 3D model", "Make Store 3D Available"]

const parseRequestedPage = (locationSearch: string) => {
  const value = Number(new URLSearchParams(locationSearch).get("page"))
  return Number.isInteger(value) && value > 0 ? value : 1
}

// Draft = a row never successfully saved; otherwise show model completeness.
// Reuse the same dot + label treatment as Makeup and Glasses product lists.
const StatusBadge = ({ item }: { item: Viewer3DListItem }) => {
  // The design calls for a regular-weight status label in this list.
  const weight = "font-normal"
  if (item.draft)
    return (
      <ProductStatusBadge variant="needSetup" className={weight}>
        Draft
      </ProductStatusBadge>
    )
  if (item.status === "ready")
    return (
      <ProductStatusBadge variant="ready" className={weight}>
        Ready
      </ProductStatusBadge>
    )
  if (item.status === "processing")
    return (
      <ProductStatusBadge variant="processing" className={weight}>
        Processing
      </ProductStatusBadge>
    )
  return (
    <ProductStatusBadge variant="needSetup" className={weight}>
      Needs setup
    </ProductStatusBadge>
  )
}

export const Viewer3DProductsPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { showAlert } = useAlert()
  const merchantId = useAppSelector(selectUserInfo)?.merchantId ?? ""
  const { data, isError, isLoading, refetch } = useViewer3DProductsQuery()
  const togglePublish = useToggleViewer3DPublishMutation()
  const ensureDemoKey = useEnsureViewer3DDemoKeyMutation()
  const deleteProduct = useDeleteViewer3DProductMutation()
  const [search, setSearch] = useState("")
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)
  const [bulkBusy, setBulkBusy] = useState(false)
  const [pageSize, setPageSize] = useState(() => {
    const stored = Number(localStorage.getItem(PAGE_SIZE_STORAGE_KEY))
    return PAGE_SIZE_OPTIONS.includes(stored) ? stored : PAGE_SIZE_OPTIONS[0]
  })

  const resetPagination = useCallback(() => {
    const params = new URLSearchParams(location.search)
    if (!params.has("page")) return

    params.delete("page")
    navigate(`${location.pathname}${params.toString() ? `?${params}` : ""}`, { replace: true })
  }, [location.pathname, location.search, navigate])

  const previousMerchantId = useRef(merchantId)
  const merchantScopeChanged = Boolean(
    previousMerchantId.current && merchantId && previousMerchantId.current !== merchantId
  )
  const requestedPage = parseRequestedPage(location.search)
  const allItems = data?.items ?? []
  const hasActiveSearch = search.trim().length > 0
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return q ? allItems.filter((item) => item.name.toLowerCase().includes(q)) : allItems
  }, [allItems, search])

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize))
  const currentPage = merchantScopeChanged ? 1 : Math.min(requestedPage, pageCount)
  const pageItems = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize)

  const hasProducts = allItems.length > 0
  const showEmptyState = !isLoading && !isError && !hasProducts && !hasActiveSearch

  const pageItemIds = pageItems.map((item) => item.productId)
  const selectedCount = selectedIds.size
  const allSelected = pageItemIds.length > 0 && pageItemIds.every((id) => selectedIds.has(id))
  const indeterminate = selectedCount > 0 && !allSelected
  const showSelectionBar = selectedCount > 0

  const toggleRowSelection = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  /**
   * The mutation had no error path at all, and the Switch is fully controlled —
   * so a rejected publish left the thumb where it was and said nothing. The
   * server names the reason; show it instead of a generic failure.
   */
  const handleTogglePublish = async (productId: string, publish: boolean) => {
    try {
      await togglePublish.mutateAsync({ productId, publish })
    } catch (error) {
      const code = error instanceof AdminRequestError ? error.code : null
      showAlert({
        error: true,
        text:
          code === "viewer3d_not_publishable"
            ? "Turn on the 3D button for at least one variant that has a model first."
            : code === "viewer3d_version_conflict"
            ? "This product changed elsewhere. Refreshing the list."
            : `Couldn't ${publish ? "turn on" : "turn off"} the 3D button. Please try again.`,
      })
      throw error
    }
  }

  const handleToggleAll = (checked: boolean) =>
    setSelectedIds(checked ? new Set(pageItemIds) : new Set())

  const clearSelection = () => setSelectedIds(new Set())

  // Selection is scoped to what's currently visible — reset it when that changes.
  useEffect(() => {
    setSelectedIds(new Set())
  }, [currentPage, merchantId, pageSize, search])

  useEffect(() => {
    const previous = previousMerchantId.current
    previousMerchantId.current = merchantId
    if (!previous || !merchantId || previous === merchantId) return
    resetPagination()
  }, [merchantId, resetPagination])

  useEffect(() => {
    if (!data || requestedPage === currentPage) return

    const params = new URLSearchParams(location.search)
    if (currentPage === 1) params.delete("page")
    else params.set("page", String(currentPage))
    navigate(`${location.pathname}${params.toString() ? `?${params}` : ""}`, { replace: true })
  }, [currentPage, data, location.pathname, location.search, navigate, requestedPage])

  useEffect(() => {
    if (selectedIds.size === 0) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSelectedIds(new Set())
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [selectedIds.size])

  // The current ?page rides along into the editor so every exit path there
  // can bring the merchant back to the page they left (see utils/withListPage).
  const goAdd = () => navigate(withListPage("/3d-viewer/add", location.search))
  const goEdit = (productId: string) =>
    navigate(withListPage(`/3d-viewer/edit?id=${encodeURIComponent(productId)}`, location.search))
  // "Share" lives on the Integration page, which mints the publishable
  // key/demo link and hosts the shopper stand for a published product.
  const goIntegration = () => navigate("/3d-viewer/integration")

  // "View demo" opens the shopper stand directly with the merchant's reusable
  // demo token (minted once, allowlisted for every platform stand) and the
  // product's merchant-facing id — the Merchant Variant ID of its first showable
  // variant, since products no longer carry one of their own. The tab is opened
  // synchronously (before the await) to survive pop-up blockers. Note "noopener" cannot be
  // passed to window.open here — it makes the call return null and the handle
  // is needed to redirect the tab — so the opener is severed manually.
  const openDemo = async (item: Viewer3DListItem) => {
    const demoTarget = resolveViewer3DDemoTarget(item)
    if (!demoTarget) {
      goIntegration()
      return
    }

    const demoWindow = window.open("about:blank", "_blank")
    if (!demoWindow) {
      showAlert({ error: true, text: "Allow pop-ups to open the demo." })
      return
    }
    demoWindow.opener = null

    try {
      const { token } = await ensureDemoKey.mutateAsync()
      if (!demoWindow.closed) demoWindow.location.replace(buildViewer3DDemoUrl(token, demoTarget))
    } catch {
      if (!demoWindow.closed) demoWindow.close()
      showAlert({ error: true, text: "Failed to open the demo. Please try again." })
    }
  }

  const handleSearchChange = (value: string) => {
    setSearch(value)
    resetPagination()
  }

  const handlePageSizeChange = (value: string) => {
    const size = Number(value)
    setPageSize(size)
    localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(size))
    resetPagination()
  }

  const performBulkDelete = async () => {
    const ids = [...selectedIds]
    if (ids.length === 0) {
      setBulkDeleteOpen(false)
      return
    }
    setBulkDeleteOpen(false)
    clearSelection()
    setBulkBusy(true)
    try {
      await Promise.all(ids.map((id) => deleteProduct.mutateAsync(id)))
      showAlert({
        error: false,
        text: ids.length === 1 ? "Product deleted" : `${ids.length} products deleted`,
      })
    } catch {
      showAlert({ error: true, text: "Couldn't delete some products. Please try again." })
    } finally {
      setBulkBusy(false)
    }
  }

  return (
    <div className="mx-auto max-w-[1440px] p-6">
      <AlertDialog open={bulkDeleteOpen} onOpenChange={(open) => !open && setBulkDeleteOpen(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {selectedCount === 1 ? "Delete product?" : `Delete ${selectedCount} products?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {selectedCount === 1
                ? "This will remove the product, all its variants, 3D files, and settings. This action can't be undone."
                : `This will remove ${selectedCount} products, all their variants, 3D files, and settings. This action can't be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => void performBulkDelete()}
            >
              {selectedCount === 1 ? "Delete product" : "Delete products"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className={showEmptyState ? "mb-6 border-b pb-6" : "mb-6"}>
        <div className="flex items-start justify-between gap-3">
          <div>
            {showEmptyState ? (
              <>
                <h1 className="text-2xl font-semibold tracking-tight">
                  Welcome! Your 3D viewer is almost ready
                </h1>
                <p className="mt-2 text-sm text-muted-foreground">Add your first product below</p>
              </>
            ) : (
              <>
                <h1 className="text-2xl font-semibold tracking-tight">
                  Set Up 3D viewer for Your Products
                </h1>
                <ol className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-2 text-sm text-muted-foreground">
                  {SETUP_STEPS.map((step, index) => (
                    <li key={step} className="flex items-center gap-2">
                      {index > 0 && (
                        <span aria-hidden className="text-muted-foreground/50">
                          &bull;
                        </span>
                      )}
                      <span>
                        {index + 1}. {step}
                      </span>
                    </li>
                  ))}
                </ol>
              </>
            )}
          </div>
        </div>

        {hasProducts && (
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-1 items-center gap-3">
              <div className="relative min-w-[240px] max-w-sm flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  name="searchbar"
                  value={search}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    handleSearchChange(e.target.value)
                  }
                  placeholder="Search products"
                  aria-label="Search 3D products"
                  className="h-9 pl-9 text-sm"
                />
                {search && (
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-muted-foreground hover:text-foreground"
                    onClick={() => handleSearchChange("")}
                    aria-label="Clear search"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Show</span>
                <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="h-8 w-[75px]" aria-label="3D products per page">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button
              onClick={goAdd}
              className="bg-foreground text-background shadow-sm hover:bg-foreground/90 focus-visible:ring-foreground"
            >
              <Plus className="mr-1.5 h-4 w-4" />
              Add product
            </Button>
          </div>
        )}
      </div>

      {hasProducts && !isLoading ? (
        pageItems.length > 0 ? (
          <>
            <Card className="overflow-hidden">
              <Table className="table-fixed">
                <colgroup>
                  <col className="w-[44px]" />
                  <col />
                  <col className="w-[120px]" />
                  <col className="w-[130px]" />
                  <col className="w-[190px]" />
                  <col className="w-[340px]" />
                </colgroup>
                <TableHeader>
                  {showSelectionBar ? (
                    <Viewer3DSelectionToolbar
                      selectedCount={selectedCount}
                      allSelected={allSelected}
                      indeterminate={indeterminate}
                      onToggleAll={handleToggleAll}
                      onDelete={() => setBulkDeleteOpen(true)}
                      onClear={clearSelection}
                      deleting={bulkBusy}
                    />
                  ) : (
                    <TableRow className="group bg-muted/50 hover:bg-muted/50">
                      <TableHead className="pl-3">
                        <HeaderSelectAllCheckbox
                          allSelected={allSelected}
                          indeterminate={indeterminate}
                          onToggle={handleToggleAll}
                        />
                      </TableHead>
                      <TableHead className="text-sm text-foreground">Product</TableHead>
                      <TableHead className="text-sm text-foreground">Type</TableHead>
                      <TableHead className="text-sm text-foreground">Status</TableHead>
                      <TableHead className="text-sm text-foreground">3D Button</TableHead>
                      <TableHead className="text-right text-sm text-foreground">Actions</TableHead>
                    </TableRow>
                  )}
                </TableHeader>
                <TableBody>
                  {pageItems.map((item) => (
                    <Viewer3DRow
                      key={item.productId}
                      item={item}
                      selected={selectedIds.has(item.productId)}
                      onToggleSelect={toggleRowSelection}
                      onTogglePublish={(publish) => handleTogglePublish(item.productId, publish)}
                      onEdit={() => goEdit(item.productId)}
                      onViewDemo={() => void openDemo(item)}
                      onShare={goIntegration}
                    />
                  ))}
                </TableBody>
              </Table>
              {pageCount > 1 && (
                <div className="border-t px-4 py-3">
                  <Paginator page={currentPage} pageCount={pageCount} />
                </div>
              )}
            </Card>
            <p className="mt-4 text-sm text-muted-foreground">
              {selectedCount} of {filtered.length} row(s) selected.
            </p>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed p-12">
            <p className="text-sm text-muted-foreground">
              No products match &ldquo;{search}&rdquo;
            </p>
            <Button variant="outline" size="sm" onClick={() => handleSearchChange("")}>
              Clear search
            </Button>
          </div>
        )
      ) : null}

      {isError && !isLoading && (
        <div
          className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed p-12"
          role="alert"
        >
          <p className="text-sm text-muted-foreground">Couldn&apos;t load 3D products.</p>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Try again
          </Button>
        </div>
      )}

      {/* Same component and the same wrapper padding as the makeup/glasses
          first-run states — the three pages must not drift apart again. */}
      {showEmptyState && (
        <div className="flex min-h-[60vh] items-center justify-center py-12">
          <EmptyList
            icon={<Rotate3d className="size-10" />}
            title="Add your first product in 3D"
            addText="Add product"
            onClick={goAdd}
            showAdd
          />
        </div>
      )}

      {isLoading && (
        <Card className="overflow-hidden">
          <div className="space-y-5 p-6">
            <div className="flex items-center justify-between gap-3">
              <Skeleton width="w-40" height="h-6" />
              <Skeleton width="w-28" height="h-9" />
            </div>
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, index) => (
                <div
                  key={index}
                  className="grid grid-cols-[44px_1fr_120px_130px_190px_340px] items-center gap-4 border-b pb-3 last:border-b-0 last:pb-0"
                >
                  <Skeleton width="w-4" height="h-4" />
                  <Skeleton width="w-48" height="h-10" />
                  <Skeleton width="w-16" height="h-4" />
                  <Skeleton width="w-20" height="h-6" />
                  <Skeleton width="w-24" height="h-4" />
                  <Skeleton width="w-full" height="h-8" />
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}
    </div>
  )
}

const HeaderSelectAllCheckbox = ({
  allSelected,
  indeterminate,
  onToggle,
}: {
  allSelected: boolean
  indeterminate: boolean
  onToggle: (checked: boolean) => void
}) => (
  <Checkbox
    aria-label="Select all"
    checked={indeterminate ? "indeterminate" : allSelected}
    className={
      allSelected || indeterminate
        ? ""
        : "opacity-0 transition-opacity group-hover:opacity-60 group-hover:hover:opacity-100 group-focus-within:opacity-100"
    }
    onCheckedChange={(value) => onToggle(Boolean(value))}
  />
)

interface Viewer3DSelectionToolbarProps {
  selectedCount: number
  allSelected: boolean
  indeterminate: boolean
  onToggleAll: (checked: boolean) => void
  onDelete: () => void
  onClear: () => void
  deleting: boolean
}

const Viewer3DSelectionToolbar = ({
  selectedCount,
  allSelected,
  indeterminate,
  onToggleAll,
  onDelete,
  onClear,
  deleting,
}: Viewer3DSelectionToolbarProps) => (
  <TableRow className="border-b bg-vto-accent-soft hover:bg-vto-accent-soft">
    <TableHead className="h-10 pl-3 align-middle">
      <Checkbox
        aria-label="Toggle all"
        checked={indeterminate ? "indeterminate" : allSelected}
        onCheckedChange={(value) => onToggleAll(Boolean(value))}
      />
    </TableHead>
    <TableHead colSpan={5} className="h-10 align-middle font-normal">
      <div className="flex h-10 items-center gap-3">
        <span className="text-sm font-medium text-foreground">{selectedCount} selected</span>
        <span className="h-5 w-px bg-border" aria-hidden />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1.5 px-2 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
          disabled={deleting}
          onClick={onDelete}
        >
          <Trash2 className="size-3.5" />
          Delete
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="ml-auto h-7 gap-1.5 px-2 text-xs text-muted-foreground"
          onClick={onClear}
        >
          <X className="size-3.5" />
          Clear
        </Button>
      </div>
    </TableHead>
  </TableRow>
)

interface Viewer3DRowProps {
  item: Viewer3DListItem
  selected: boolean
  onToggleSelect: (id: string) => void
  onTogglePublish: (publish: boolean) => Promise<void>
  onEdit: () => void
  onViewDemo: () => void
  onShare: () => void
}

const Viewer3DRowComponent = ({
  item,
  selected,
  onToggleSelect,
  onTogglePublish,
  onEdit,
  onViewDemo,
  onShare,
}: Viewer3DRowProps) => {
  // Per-row, not the shared mutation state: one in-flight toggle used to disable
  // every switch on the page, which read as "the wrong toggle reacted".
  const [publishPending, setPublishPending] = useState(false)
  const isReady = item.status === "ready"

  const handleTogglePublish = async (publish: boolean) => {
    if (publishPending) return
    setPublishPending(true)
    try {
      await onTogglePublish(publish)
    } catch {
      // The mutation's onError rolls the row back and raises the toast.
    } finally {
      setPublishPending(false)
    }
  }
  // Demo/share are only meaningful for a live (published) product; mirror the
  // design where they read as disabled while the 3D button is Off.
  const canShare = item.published

  const handleRowClick = (event: MouseEvent<HTMLTableRowElement>) => {
    const target = event.target as HTMLElement
    if (
      target.closest("button") ||
      target.closest('[role="switch"]') ||
      target.closest('[role="checkbox"]') ||
      target.closest("label")
    )
      return
    onToggleSelect(item.productId)
  }

  return (
    <TableRow
      data-state={selected ? "selected" : undefined}
      className="group cursor-pointer data-[state=selected]:bg-vto-accent-soft"
      onClick={handleRowClick}
    >
      <TableCell className="pl-3">
        <Checkbox
          aria-label={`Select ${item.name}`}
          checked={selected}
          onCheckedChange={() => onToggleSelect(item.productId)}
          className={
            selected
              ? ""
              : "opacity-0 transition-opacity group-hover:opacity-60 group-hover:hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100"
          }
        />
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-md bg-muted/40">
            {item.previewImageUrl ? (
              <img src={item.previewImageUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              <Box className="h-5 w-5 text-muted-foreground/60" />
            )}
          </div>
          <span className="max-w-[320px] truncate text-sm font-medium">{item.name}</span>
        </div>
      </TableCell>
      <TableCell>
        <span className="text-sm text-muted-foreground">{item.category}</span>
      </TableCell>
      <TableCell>
        <StatusBadge item={item} />
      </TableCell>
      <TableCell onClick={(event) => event.stopPropagation()}>
        <div className="flex items-center gap-2">
          <Switch
            className="cursor-pointer"
            checked={item.published}
            disabled={!isReady || publishPending}
            onCheckedChange={(checked) => void handleTogglePublish(checked)}
            aria-label={`Toggle 3D button for ${item.name}`}
          />
          <span className="whitespace-nowrap text-sm text-muted-foreground">
            {item.published
              ? item.variantCount > 1
                ? `On (${item.enabledVariantCount} of ${item.variantCount})`
                : "On"
              : "Off"}
          </span>
        </div>
      </TableCell>
      <TableCell onClick={(event) => event.stopPropagation()}>
        <div className="flex items-center justify-end gap-0.5">
          <Button
            variant="ghost"
            size="sm"
            disabled={!canShare}
            className="h-8 gap-1.5 px-2 text-muted-foreground hover:text-foreground"
            onClick={onViewDemo}
          >
            <Eye className="size-4" />
            View demo
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={!canShare}
            className="h-8 gap-1.5 px-2 text-muted-foreground hover:text-foreground"
            onClick={onShare}
          >
            <Share2 className="size-4" />
            Share
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 gap-1.5 px-2 text-muted-foreground hover:text-foreground"
            onClick={onEdit}
          >
            <Pencil className="size-4" />
            Set up
          </Button>
        </div>
      </TableCell>
    </TableRow>
  )
}

// Memoized so an optimistic single-row patch does not re-render (and re-mount
// the model preview of) every other row on the page.
const Viewer3DRow = memo(Viewer3DRowComponent)
